import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useRemoteParticipants, useRoomContext, useRoomInfo } from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'
import {
  useEventLiveKit,
  useEventLiveKitLivePublisherIdentities,
  useEventLiveKitPublishingIdentities,
  useEventLiveKitVideoSources,
} from '@/components/livekit/EventLiveKitProvider'
import { mergeViewerCameraTelemetry, visibleViewerCameras, type ViewerPayload } from '@/lib/comrades/viewerPayload'
import { useStreamTelemetry } from '@/hooks/useStreamTelemetry'
import { watchApi } from './api'
import { cameraOrder, hostSources, parseRoomMetadata, resolveWatchedIdentity, sourceTrack, stageFromPayload, stageLive, studioFaces } from './rules'
import type { StageFace, StageLive, StageOwner, StagePayload, WatchCamera, WatchConnectionState } from './types'
import { useCohostIdentity } from './useCohost'
import { MEMBERSHIP_ONLY, othersWatching as othersIn } from './useOnline'

/**
 * How long a camera's live GPS point (`telemetry.location` data) stands before the API's last saved location takes over
 * again: a few missed updates, not a stopped phone.
 */
const LIVE_LOCATION_MS = 15_000

export interface WatchScreenInput {
  payload: ViewerPayload
  stage: StagePayload
  /** The room's connection, from `WatchRoom`. */
  connection: WatchConnectionState
}

export interface WatchScreen {
  /**
   * The event's cameras in `cameraOrder`, online only while LiveKit has their video, waiting while it lists their video
   * but this viewer has none yet. A `live_only` event leaves out the ones that are neither. With them, the stage's live
   * hosts' shared screens (`hostSources`), each while it is shared. A host in Studio mode is a face, not one of these.
   */
  cameras: WatchCamera[]
  /** The stage's faces and called camera: the room metadata's once present, the stage payload's before. */
  live: StageLive
  /**
   * The stage's live members as the room metadata lists them, a new array with each metadata update; null while the
   * metadata carries no stages (the payload's members don't count). For the host's own seat (`useStagePresence`).
   */
  roomMembers: StageFace[] | null
  /**
   * Every stage the room metadata lists, a new object with each metadata update; null while it carries none (an old
   * pod's metadata, or none published yet). For the stage switcher and a host live on another stage than the one shown.
   */
  roomStages: Record<string, StageLive> | null
  /** Whose stage it is, so its owner's face leads (`StageFaces`): the metadata's word, the payload's before it. */
  owner: StageOwner
  /** The camera the host's audience follows. A call this tab just made counts until the room metadata moves. */
  calledIdentity: string | null
  /** This viewer hosts the stage (`viewer.can_host`): a tap previews instead of overriding. */
  isHost: boolean
  /**
   * The camera on the canvas: online, or, when nothing is online, the one it was meant to show: while the room is
   * connected, the viewer's pick, the host's call or the featured camera (offline or on its way), else the first camera
   * on its way; while it reconnects (LiveKit drops every track meanwhile), the one it showed last while connected, or a
   * camera picked since. Null before the room connects and once it's lost.
   */
  watched: WatchCamera | null
  watchedIdentity: string | null
  /** A viewer is watching their own pick, not the host's call: Back to host shows. */
  overridden: boolean
  /**
   * A camera of this viewer's own choosing is on the canvas: their pick, or a host's preview. A host calling their map
   * doesn't take the canvas from it (docs/features/follow-host-map.md).
   */
  picked: boolean
  /** The camera a host is looking at that their audience isn't (Preview, Show my audience); null otherwise. */
  previewIdentity: string | null
  /** A camera was tapped: a viewer's own pick, or a host's preview. */
  select(identity: string): void
  /** Drop the viewer's pick and follow the host's call again. */
  backToHost(): void
  /** Show my audience: calls `identity` for the stage (null clears the call). Rejects when the API refuses; the preview stays then. */
  callCamera(identity: string | null): Promise<void>
  /**
   * Audible: the watched camera, every live face, and the stage's Badger while he is in the room. Stable while its
   * contents are, for `LayoutAudioGate`.
   */
  audioIdentities: readonly string[]
  /**
   * The stage's Badger while he is in the room (docs/features/badger-cohost.md, Phase 2 contract: an agent participant
   * whose `stage_id` attribute names the stage), else null. Asked for or not: presence comes from the room alone.
   */
  cohostIdentity: string | null
  /**
   * The participant heard under the faces (docs/features/stage-volume.md): the watched camera or Live show, by its
   * LiveKit identity. Null while the canvas shows a face's own source (a Studio face, a host's screen or map, heard
   * through its host) or nothing.
   */
  sourceIdentity: string | null
  /** How loud that source plays for the stage's audience, 0–100: the host's level while someone is live on the stage, else 100. */
  sourceVolume: number
  /**
   * What the stage's audience follows with nothing picked (the host's call, else the Live show or camera the screen falls
   * back to), when it is a camera or the Live show; null for a face's own source or nothing. The host's source volume
   * names it, whatever the host is previewing.
   */
  audienceSource: WatchCamera | null
  /** The world layer's version as the room metadata has it (`useWorldLayer` fetches a newer layer); null before it says. */
  worldVersion: number | null
}

/**
 * The watch screen's state, where the screen's rules (`rules.ts`) meet live data: the viewer payload's cameras marked
 * online from LiveKit, the room metadata's stage (the stage payload's until it arrives), and the viewer's pick or the
 * host's preview. Use inside `WatchRoom` (the room's context). The audience is `useAudience`. The same object comes
 * back while none of its parts change. `stage` is the stage on screen: switching to another stage of the event drops
 * the viewer's pick, the host's preview and a call this tab made, and follows the new stage's host (the same room).
 */
export function useWatchScreen({ payload, stage, connection }: WatchScreenInput): WatchScreen {
  const stageId = stage.stage.id
  const isHost = Boolean(stage.viewer?.can_host)

  // Cameras: rebuilt only when which of them have video (or have it on its way) changes, not when another publisher (a
  // camera's telemetry, a face) comes or goes (a new array would re-run the map's follow ease). A camera on its way is
  // not offline, so `live_only` shows it too.
  const liveIdentities = useEventLiveKitLivePublisherIdentities()
  const publishingIdentities = useEventLiveKitPublishingIdentities()
  const shownIdentities = useMemo<ReadonlySet<string>>(
    () => (publishingIdentities.size ? new Set([...liveIdentities, ...publishingIdentities]) : liveIdentities),
    [liveIdentities, publishingIdentities],
  )
  const visible = visibleViewerCameras(payload, shownIdentities)
  // Cameras send their GPS as room data; the payload only has the last location the API saved (often none while a
  // camera is live). A fresh point wins, so a camera's sphere appears and follows it as it moves.
  const telemetry = useStreamTelemetry(payload.event.id, { staleThresholdMs: LIVE_LOCATION_MS })
  const located = useMemo(() => mergeViewerCameraTelemetry(payload.cameras, telemetry), [payload.cameras, telemetry])
  const keyOf = (keep: (identity: string) => boolean) =>
    visible
      .map((camera) => camera.participant_identity)
      .filter(keep)
      .join('\n')
  const onlineKey = keyOf((identity) => liveIdentities.has(identity))
  const waitingKey = keyOf((identity) => publishingIdentities.has(identity) && !liveIdentities.has(identity))
  const visibleKey = keyOf(() => true)
  const eventCameras = useMemo(() => {
    const set = (key: string) => new Set(key ? key.split('\n') : [])
    const online = set(onlineKey)
    const waiting = set(waitingKey)
    const shown = set(visibleKey)
    return located
      .filter((camera) => camera.participant_identity && shown.has(camera.participant_identity))
      .map((camera): WatchCamera => ({
        identity: camera.participant_identity,
        name: camera.name,
        sourceRole: camera.source_role === 'programme' ? 'programme' : 'camera',
        online: online.has(camera.participant_identity),
        waiting: waiting.has(camera.participant_identity),
        location: camera.location ?? null,
      }))
  }, [located, onlineKey, waitingKey, visibleKey])

  // The stage: the room's once its metadata carries stages, the loaded payload's before.
  const lkRoom = useRoomContext()
  const { metadata } = useRoomInfo()
  const room = useMemo(() => parseRoomMetadata(metadata), [metadata])
  const loaded = useMemo(() => stageFromPayload(stage.stage), [stage.stage])
  const live = stageLive(room, stageId, loaded)

  // The stage's hosts' shared screens join the cameras, and its Studio faces can be watched (picked or called), each
  // rebuilt only when what they show changes (a screen shared, Studio mode, their video coming or going), not with every
  // other source's video.
  const videoSources = useEventLiveKitVideoSources()
  const hostKey = JSON.stringify(hostSources(live.members, videoSources.live, videoSources.publishing))
  const cameras = useMemo(
    () => [...eventCameras, ...(JSON.parse(hostKey) as WatchCamera[])].sort(cameraOrder),
    [eventCameras, hostKey],
  )
  const studioKey = JSON.stringify(studioFaces(live.members, videoSources.live, videoSources.publishing))
  const studios = useMemo(() => JSON.parse(studioKey) as WatchCamera[], [studioKey])
  const roomMembers = useMemo(() => (room?.stages ? room.stages[stageId]?.members ?? [] : null), [room, stageId])
  const roomStages = room?.stages ?? null
  const worldVersion = room?.worldVersion ?? null
  const featured = room ? room.featured : payload.featured_participant_identity
  // The metadata's word on whose stage it is, the payload's while it says nothing (an old pod, or not listed yet).
  const ownerKind = live.kind ?? loaded.kind ?? null
  const ownerUserId = live.ownerUserId ?? loaded.ownerUserId ?? null
  const owner = useMemo<StageOwner>(() => ({ kind: ownerKind, userId: ownerUserId }), [ownerKind, ownerUserId])

  // A call this tab made counts until the room's call moves from what it was when asked (to it, or past it).
  const [answer, setAnswer] = useState<{ identity: string | null; roomCalled: string | null } | null>(null)
  // The viewer's pick, or the host's preview. Once the host calls it, it's the host's call again: the viewer follows
  // the host's next one, and the host's preview is over.
  const [override, setOverride] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  // Another stage: its host's call, nothing picked on the last one (the prototype's switch).
  const [pickedOn, setPickedOn] = useState(stageId)
  if (pickedOn !== stageId) {
    setPickedOn(stageId)
    setAnswer(null)
    setOverride(null)
    setPreview(null)
  }
  if (answer && answer.roomCalled !== live.calledIdentity) setAnswer(null)
  const calledIdentity = answer && answer.roomCalled === live.calledIdentity ? answer.identity : live.calledIdentity

  // What the screen shows with nothing picked (`resolveWatchedIdentity`): a live host's call, else a live Live show, …
  const hostOnStage = live.members.some((member) => member.role === 'host')
  const followed = resolveWatchedIdentity({ override: null, called: calledIdentity, hostOnStage, featured, cameras, faces: studios })
  // Once the host calls the camera the viewer picked (and the screen follows the host), it's the host's call again: the
  // viewer follows the host's next one.
  if (override !== null && override === calledIdentity && followed === calledIdentity) setOverride(null)
  if (preview !== null && preview === calledIdentity) setPreview(null)
  const pick = isHost ? preview : override

  const resolved = resolveWatchedIdentity({ override: pick, called: calledIdentity, hostOnStage, featured, cameras, faces: studios })
  // A Studio face counts as the pick or the call (named, offline or on its way), never as the first camera on its way.
  const byIdentity = (identity: string | null) =>
    identity ? cameras.find((camera) => camera.identity === identity) ?? studios.find((face) => face.identity === identity) ?? null : null
  const resolvedCamera = byIdentity(resolved)
  // Nothing online while connected: keep the camera the canvas was meant to show, so it says Offline under its name (or
  // waits for it), not that every camera is offline; with none meant, the first camera on its way, so the canvas waits
  // for the picture that's coming.
  const meant =
    resolvedCamera ??
    byIdentity(pick) ??
    byIdentity(calledIdentity) ??
    byIdentity(featured) ??
    cameras.find((camera) => camera.waiting) ??
    null
  const meantIdentity = meant?.identity ?? null
  // While the room reconnects, the camera shown last while connected (with the pick of that moment): the fallbacks
  // above can name another camera than the one that was on screen (the host's offline call, when the canvas had fallen
  // back to the first online camera). A pick made since wins. Before the room connects, or once it's lost, nothing is
  // online for another reason, and the canvas says so.
  const [held, setHeld] = useState<{ identity: string | null; pick: string | null }>({ identity: null, pick: null })
  if (connection === 'connected' && (held.identity !== meantIdentity || held.pick !== pick)) setHeld({ identity: meantIdentity, pick })
  const watched =
    connection === 'connected'
      ? meant
      : connection === 'reconnecting'
        ? resolvedCamera ?? byIdentity(pick !== held.pick ? pick : held.identity)
        : resolvedCamera
  const watchedIdentity = watched?.identity ?? null
  // Left the host's call, while the host's call is what the screen follows (Back to host returns to it).
  const overridden =
    !isHost && override !== null && calledIdentity !== null && followed === calledIdentity && override !== calledIdentity && watchedIdentity === override
  // Only while the canvas shows it: Show my audience calls what the host sees.
  const previewIdentity = isHost && preview !== null && watchedIdentity === preview ? preview : null
  const picked = pick !== null && watchedIdentity === pick

  const select = useCallback((identity: string) => (isHost ? setPreview(identity) : setOverride(identity)), [isHost])
  const backToHost = useCallback(() => setOverride(null), [])

  const roomCalledRef = useRef(live.calledIdentity)
  useLayoutEffect(() => {
    roomCalledRef.current = live.calledIdentity
  })
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const stageRef = useRef(stageId)
  useLayoutEffect(() => {
    stageRef.current = stageId
  })
  const callCamera = useCallback(async (identity: string | null) => {
    const roomCalled = roomCalledRef.current
    const { data } = await watchApi.callCamera(stageId, identity)
    // The viewer switched stage meanwhile: the answer is the other stage's call.
    if (!mountedRef.current || stageRef.current !== stageId) return
    setAnswer({ identity: data.called_identity, roomCalled })
    setPreview(null)
  }, [stageId])

  // A host's screen is heard through its host, who is on the stage anyway. The stage's Badger is heard by its audience,
  // as its hosts are; other stages' viewers don't subscribe to him.
  const cohostIdentity = useCohostIdentity(lkRoom, stageId)
  const watchedParticipant = watchedIdentity ? sourceTrack(watchedIdentity).identity : ''
  const audioKey = [watchedParticipant, ...live.members.map((member) => member.identity), cohostIdentity ?? ''].join('\n')
  const audioIdentities = useMemo(() => {
    const [watchedId, ...faces] = audioKey.split('\n')
    return Array.from(new Set([watchedId, ...faces].filter(Boolean)))
  }, [audioKey])
  // The source under the faces: what's watched, unless it is a face's own (heard through its face, at the viewer's volume).
  const onStage = live.members.length > 0
  const sourceIdentity = watchedParticipant && !live.members.some((member) => member.identity === watchedParticipant) ? watchedParticipant : null
  const sourceVolume = onStage ? (live.sourceVolume ?? 100) : 100
  const followedCamera = followed ? (cameras.find((camera) => camera.identity === followed) ?? null) : null
  const audienceSource = followedCamera && (followedCamera.sourceRole === 'camera' || followedCamera.sourceRole === 'programme') ? followedCamera : null

  // One object while nothing in it changes, so a memoised stage under the page skips the page's other renders.
  return useMemo(
    () => ({
      cameras,
      live,
      roomMembers,
      roomStages,
      owner,
      calledIdentity,
      isHost,
      watched,
      watchedIdentity,
      overridden,
      picked,
      previewIdentity,
      select,
      backToHost,
      callCamera,
      audioIdentities,
      cohostIdentity,
      sourceIdentity,
      sourceVolume,
      audienceSource,
      worldVersion,
    }),
    [cameras, live, roomMembers, roomStages, owner, calledIdentity, isHost, watched, watchedIdentity, overridden, picked, previewIdentity, select, backToHost, callCamera, audioIdentities, cohostIdentity, sourceIdentity, sourceVolume, audienceSource, worldVersion],
  )
}

export interface Audience {
  /** People watching, this viewer included, once connected; null before. */
  audience: number | null
  /** People watching besides this viewer, for the host's camera check. */
  othersWatching: number
}

/**
 * How many people are watching: the room's watchers as this tab sees them, each person once however many tabs they
 * have open (`watcherKey`, as the online tray counts them), while connected (a signal resume counts, as it does for
 * the canvas: the media keeps flowing). This viewer's other tabs aren't others. It re-renders on every arrival and
 * departure, so call it in the small parts that show the count, not the page. Use inside `WatchRoom`.
 */
export function useAudience(): Audience {
  const { connectionState } = useEventLiveKit()
  const self = useRoomContext().localParticipant?.identity
  const remote = useRemoteParticipants({ updateOnlyOn: MEMBERSHIP_ONLY })
  const othersWatching = othersIn(remote.map((participant) => participant.identity), self)
  const connected = connectionState === ConnectionState.Connected || connectionState === ConnectionState.SignalReconnecting
  return { audience: connected ? othersWatching + 1 : null, othersWatching }
}
